/* eslint-disable no-unused-expressions */
import { createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from 'react-toastify';
import { API_TOKEN, API_URL } from "../../Config/secrets";

// ----------------------------------------------------------------------

const initialState = {
  isLoading: false,
  error: null,
  user: null,
  isAuthenticated: false,
  adminUsers: {},
  totalAdminUsers: 0,
};

const slice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },
    stopLoading(state) {
      state.isLoading = false;
    },
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },
    // LOGIN SUCCESS
    loginSuccess(state, action) {
      state.isLoading = false;
      state.error = null;
      state.user = action.payload;
      state.isAuthenticated = true;
    },
    // LOGOUT
    logout(state) {
      state.user = null;
      state.isAuthenticated = false;
      state.error = null;
    },
    // INVITE USER SUCCESS
    inviteUserSuccess(state) {
      state.isLoading = false;
      state.error = null;
    },
    // GET ADMIN USERS SUCCESS
    getAdminUsersSuccess(state, action) {
      state.isLoading = false;
      state.error = null;
      state.totalAdminUsers = action.payload.total;
      state.adminUsers = {
        ...state.adminUsers,
        [action.payload.page]: action.payload.data,
      };
    },
    resetAuth: () => initialState,
  },
});

// Reducer
export default slice.reducer;

// Actions
export const { resetAuth, logout } = slice.actions;

// ----------------------------------------------------------------------

const header = {
  Authorization: API_TOKEN,
};

/**
 * Login admin user
 * POST /auth/login
 * @param {string} email - User email
 * @param {string} password - Plain password
 */
export function login(email, password) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.startLoading());

      const response = await axios.post(`${API_URL}/auth/login`, {
        email,
        password,
      }, {
        headers: header,
      });

      console.log('LOGIN RESPONSE', response.data);

      if (response?.data?.statusCode === 200) {
        dispatch(slice.actions.loginSuccess(response?.data?.data));
        toast.success(response?.data?.message || "Login successful", { autoClose: 3000 });
        return response?.data;
      } else {
        dispatch(slice.actions.hasError(response?.data?.message));
        toast.error(response?.data?.message || "Login failed", { autoClose: 3000 });
        return response?.data;
      }
    } catch (error) {
      console.error('LOGIN ERROR', error);
      const errorMessage = error?.response?.data?.message || error?.message || "Invalid email or password";
      toast.error(errorMessage, { autoClose: 3000 });
      dispatch(slice.actions.hasError(errorMessage));
      return error?.response?.data;
    }
  };
}

/**
 * Invite admin user
 * POST /auth/invite-user
 * @param {Object} inviteData - Invite data
 * @param {string} inviteData.email - User email (required)
 * @param {string} inviteData.password - Plain password (required)
 * @param {string} inviteData.name - Display name (optional, default "User")
 * @param {string} inviteData.role - Role (optional)
 * @param {Object} inviteData.permissions - Permissions object (required)
 */
export function inviteAdminUser(inviteData) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.startLoading());

      const response = await axios.post(`${API_URL}/auth/invite-user`, inviteData, {
        headers: header,
      });

      console.log('INVITE USER RESPONSE', response.data);

      if (response?.data?.statusCode === 200) {
        dispatch(slice.actions.inviteUserSuccess());
        toast.success(response?.data?.message || "Invitation sent successfully", { autoClose: 3000 });
        return response?.data;
      } else {
        dispatch(slice.actions.hasError(response?.data?.message));
        toast.error(response?.data?.message || "Failed to send invitation", { autoClose: 3000 });
        return response?.data;
      }
    } catch (error) {
      console.error('INVITE USER ERROR', error);
      const errorMessage = error?.response?.data?.message || error?.message || "Failed to send invitation";
      toast.error(errorMessage, { autoClose: 3000 });
      dispatch(slice.actions.hasError(errorMessage));
      return error?.response?.data;
    }
  };
}

/**
 * Get admin users
 * GET /auth/get-users/:user_id
 * @param {number} userId - Current user ID to exclude
 * @param {number} page - Page number (1-based)
 * @param {string} query - Search query (optional)
 * @param {number} take - Items per page
 * @param {boolean} hideLoader - Whether to hide the loader
 */
export function getAdminUsers(userId, page = 1, query = '', take = 10, hideLoader = false) {
  return async (dispatch) => {
    try {
      if (!hideLoader) {
        dispatch(slice.actions.startLoading());
      }

      const params = new URLSearchParams({
        skip: page.toString(),
        take: take.toString(),
        query: query || '',
      });

      const response = await axios.get(`${API_URL}/auth/get-users/${userId}?${params}`, {
        headers: header,
      });

      console.log('GET ADMIN USERS RESPONSE', response.data);

      if (response?.data?.statusCode === 200) {
        dispatch(
          slice.actions.getAdminUsersSuccess({
            page,
            total: response?.data?.data?.count || 0,
            data: response?.data?.data?.users || [],
          })
        );
        return response?.data;
      } else {
        dispatch(slice.actions.hasError(response?.data?.message));
        toast.error(response?.data?.message || "Failed to fetch admin users", { autoClose: 3000 });
        return response?.data;
      }
    } catch (error) {
      console.error('GET ADMIN USERS ERROR', error);
      const errorMessage = error?.response?.data?.message || error?.message || "Failed to fetch admin users";
      toast.error(errorMessage, { autoClose: 3000 });
      dispatch(slice.actions.hasError(errorMessage));
      return error?.response?.data;
    }
  };
}

/**
 * Delete admin user
 * DELETE /auth/user/:user_id
 * @param {number} userId - User ID to delete
 */
export function deleteAdminUser(userId) {
  return async (dispatch) => {
    try {
      const response = await axios.delete(`${API_URL}/auth/user/${userId}`, {
        headers: header,
      });

      console.log('DELETE ADMIN USER RESPONSE', response.data);

      if (response?.data?.statusCode === 200) {
        toast.success(response?.data?.message || "User deleted successfully", { autoClose: 3000 });
        return response?.data;
      } else {
        toast.error(response?.data?.message || "Failed to delete user", { autoClose: 3000 });
        dispatch(slice.actions.hasError(response?.data?.message));
        return response?.data;
      }
    } catch (error) {
      console.error('DELETE ADMIN USER ERROR', error);
      const errorMessage = error?.response?.data?.message || error?.message || "Failed to delete user";
      toast.error(errorMessage, { autoClose: 3000 });
      dispatch(slice.actions.hasError(errorMessage));
      return error?.response?.data;
    }
  };
}

/**
 * Update admin user
 * PATCH /auth/user/:user_id
 * @param {number} userId - User ID to update
 * @param {Object} updateData - Update data (all fields optional)
 * @param {string} updateData.name - Display name
 * @param {string} updateData.email - Email
 * @param {string} updateData.role - Role
 * @param {string} updateData.password - New password (will be hashed)
 * @param {Object} updateData.permissions - Permissions object
 * @param {boolean} updateData.active - Active status
 */
export function updateAdminUser(userId, updateData) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.startLoading());

      const response = await axios.patch(`${API_URL}/auth/user/${userId}`, updateData, {
        headers: header,
      });

      console.log('UPDATE ADMIN USER RESPONSE', response.data);

      if (response?.data?.statusCode === 200) {
        dispatch(slice.actions.stopLoading());
        toast.success(response?.data?.message || "User updated successfully", { autoClose: 3000 });
        return response?.data;
      } else {
        dispatch(slice.actions.hasError(response?.data?.message));
        toast.error(response?.data?.message || "Failed to update user", { autoClose: 3000 });
        return response?.data;
      }
    } catch (error) {
      console.error('UPDATE ADMIN USER ERROR', error);
      const errorMessage = error?.response?.data?.message || error?.message || "Failed to update user";
      toast.error(errorMessage, { autoClose: 3000 });
      dispatch(slice.actions.hasError(errorMessage));
      return error?.response?.data;
    }
  };
}
